import {
  Component,
  computed,
  effect,
  inject,
  model,
  OnInit,
  signal,
} from '@angular/core';
import {
  Org,
  DirectusFileSchema,
  OrgAddress,
  OrgCategory,
  OrgContact,
} from '../../schemas/org';
import { City } from '../../schemas/cityLocale';
import { OrgService } from '../../services/org.service';
import { UserService } from '../../services/user.service';
import { CountryService } from '../../utils/services/country.service';
import { HandleErrorsService } from '../../utils/services/handle-errors.service';
import { ModalService } from '../../utils/services/modal.service';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { ModalComponent } from '../modal/modal.component';
import { Locale } from '../../schemas/locale';
import { TextSearchFilterPipe } from '../../utils/pipes/text-search-filter.pipe';
import { ImageUploaderComponent } from '../image-uploader/image-uploader.component';
import { environment } from '../../../../environments/environment';
import { AccountsAppService } from '../../services/core/accounts-app.service';
import { OrgPaymentsService } from '../../services/payments.service';

@Component({
  selector: 'qwiko-edit-org-profile',
  imports: [
    CommonModule,
    TranslocoModule,
    FormsModule,
    ModalComponent,
    TextSearchFilterPipe,
    ImageUploaderComponent,
  ],
  templateUrl: './edit-org-profile.component.html',
  styleUrl: './edit-org-profile.component.scss',
})
export class EditOrgProfileComponent implements OnInit {
  private accountsAppService = inject(AccountsAppService);
  accountsAppInstance = this.accountsAppService.accountsAppInstance;

  locales = inject(CountryService);
  modalService = inject(ModalService);
  userService = inject(UserService);
  orgService = inject(OrgService);
  orgPaymentsService = inject(OrgPaymentsService);
  env = environment;
  // org =
  handleErrorsService = inject(HandleErrorsService);

  loading = model<boolean>(false);
  citiesLoaded = signal<boolean>(false);
  categoriesLoaded = signal<boolean>(false);
  isOrgProfile = signal<boolean>(true);
  org = this.orgService.org;
  orgAddress = this.orgService.address;
  orgPaymentAccounts = this.orgPaymentsService.orgPaymentAccounts;

  orgEffect = effect(() => {
    if (this.org()) {
      this.name.set(this.org()?.name!);
      this.store_name.set(this.org()?.store_name!);
      this.logo.set(this.org()?.logo! as DirectusFileSchema);
      this.banner.set(this.org()?.banner! as DirectusFileSchema);
      this.wifiName.set(this.org()?.wifi_name!);
      this.wifiPassword.set(this.org()?.wifi_password!);
      this.selectedCategory.set(this.org()?.category!);
      this.selectedCountry.set((this.org()?.country as Locale) || null);
      this.orgService.contacts().forEach((contact: OrgContact) => {
        if (contact?.is_phone_number) {
          const contactNumber = contact?.phone_number || '';
          if (contactNumber && this.selectedCountry()) {
            const code = this.selectedCountry()?.phone_code || '';
            const formattedNumber = contactNumber.replace(code, '');
            this.phoneNumber.set(formattedNumber);
            this.phoneNumberID.set(contact.id!);
          }
        } else {
          if (contact.email) {
            this.email.set(contact.email);
            this.emailID.set(contact.id!);
          }
        }
      });
    }

    if (this.orgAddress()) {
      this.selectedCity.set((this.orgAddress()?.city! as City) || null);
      this.citiesLoaded.set(true);
      this.description.set(this.orgAddress()?.street_address!);
    }
  });

  name = signal<string>('');
  store_name = signal<string>('');
  categories = signal<OrgCategory[]>([]);
  countries = signal<Locale[]>([]);
  cities = signal<City[]>([]);
  referrers = signal<any[]>([]);
  wifiName = signal<string>('');
  wifiPassword = signal<string>('');

  phoneNumberID = signal<string>('');
  phoneNumber = signal<string | null>(null);
  phoneNumberFormatted = computed<string>(() => {
    const code = this.selectedCountry()?.phone_code;
    if (!this.phoneNumber()) {
      return '';
    }
    return `${code}${this.phoneNumber()}`;
  });

  phoneNumberDisplay = computed<string>(() => {
    const code = this.selectedCountry()?.phone_code;
    const starts_with = this.selectedCountry()?.starts_with;

    if (this.phoneNumberFormatted().startsWith(code!) && starts_with) {
      const index = this.phoneNumberFormatted().indexOf(starts_with!);

      if (index !== -1) {
        const sliceIndex = index + starts_with!.length;
        const remainder = this.phoneNumberFormatted().slice(sliceIndex);

        return `${starts_with}${remainder}`;
      }
    }

    return this.phoneNumberFormatted();
  });

  callString = computed<string>(() => {
    return `tel:${this.phoneNumberFormatted()}`;
  });

  emailID = signal<string>('');
  email = signal<string>('');

  logo = signal<DirectusFileSchema | null>(null);
  logoLink = computed<string>(() => {
    return this.logo() !== null
      ? `${this.env.accountsUrl}/assets/${this.logo()!.id}/${this.logo()!.filename_disk}`
      : '';
  });
  banner = signal<DirectusFileSchema | null>(null);
  bannerLink = computed<string>(() => {
    return this.banner() !== null
      ? `${this.env.accountsUrl}/assets/${this.banner()!.id}/${this.banner()!.filename_disk}`
      : '';
  });
  countryFilter = signal<string>('');
  cityFilter = signal<string>('');
  categoryFilter = signal<string>('');
  selectedCountry = signal<Locale | null>(null);
  selectedCity = signal<City | null>(null);
  selectedReferrer = signal<any | null>(null);
  description = signal<string | null>(null);
  selectedCategory = signal<OrgCategory | null>(null);

  isEdit = signal<boolean>(false);
  isEditAddress = signal<boolean>(false);
  isEditContact = signal<boolean>(false);
  isEditWifi = signal<boolean>(false);

  isProfile = signal<boolean>(true);
  isPreview = signal<boolean>(false);
  isBasicProfile = signal<boolean>(false);
  isOrgAddress = signal<boolean>(false);
  isOrgContact = signal<boolean>(false);
  isOrgWifi = signal<boolean>(false);

  profileTabSyncEffect = effect(() => {
    if (this.isBasicProfile()) {
      this.isOrgAddress.set(false);
      this.isOrgContact.set(false);
      this.isOrgWifi.set(false);
    }
    this.resetEditStates();
  });

  addressTabSyncEffect = effect(() => {
    if (this.isOrgAddress()) {
      this.isBasicProfile.set(false);
      this.isOrgContact.set(false);
      this.isOrgWifi.set(false);
    }
    this.resetEditStates();
  });

  contactTabSyncEffect = effect(() => {
    if (this.isOrgContact()) {
      this.isBasicProfile.set(false);
      this.isOrgAddress.set(false);
      this.isOrgWifi.set(false);
    }
    this.resetEditStates();
  });

  wifiTabSyncEffect = effect(() => {
    if (this.isOrgWifi()) {
      this.isBasicProfile.set(false);
      this.isOrgAddress.set(false);
      this.isOrgContact.set(false);
    }
    this.resetEditStates();
  });

  toggleEdit() {
    this.isEdit.set(!this.isEdit());
  }
  async toggleEditAddress() {
    this.isEditAddress.set(!this.isEditAddress());
    await this.getCities(true);
  }
  toggleEditContact() {
    this.isEditContact.set(!this.isEditContact());
  }
  toggleEditWifi() {
    this.isEditWifi.set(!this.isEditWifi());
  }

  toggleTabs() {
    this.isProfile.set(!this.isProfile());
    this.isPreview.set(!this.isPreview());
  }

  async ngOnInit() {
    await this.getCountries();
  }

  async selectCountry(country: Locale) {
    this.selectedCountry.set(country);
    this.countryFilter.set('');
    this.closeModal('select-country');
    this.loading.set(true);
    this.modalService.open('select-city');
    this.cities.set([]);
    await this.getCities();
    this.loading.set(false);
  }

  selectCity(city: City) {
    this.selectedCity.set(city);
    this.cityFilter.set('');
    this.closeModal('select-city');
  }

  selectCategory(category: OrgCategory) {
    this.selectedCategory.set(category);
    this.categoryFilter.set('');
    this.closeModal('select-category');
  }

  async updateOrgProfile() {
    this.loading.set(true);

    const orgData: Org = {
      id: this.org()?.id,
      name: this.name(),
      store_name: this.store_name(),
    };

    try {
      await this.orgService.updateOrg(orgData);
      await this.updateOrgCategory();
      this.loading.set(false);
      this.isEdit.set(false);
      // this.router.navigateByUrl('/onboarding/add-accounts');
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }

    this.loading.set(false);
  }

  async updateOrgWifi() {
    this.loading.set(true);

    const orgData: Org = {
      id: this.org()?.id,
      wifi_name: this.wifiName(),
      wifi_password: this.wifiPassword(),
    };

    try {
      await this.orgService.updateOrg(orgData);
      this.loading.set(false);
      this.isEditWifi.set(false);
      // this.router.navigateByUrl('/onboarding/add-accounts');
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }

    this.loading.set(false);
  }

  async updateOrgCategory() {
    const category: OrgCategory = {
      org_id: this.org()?.id,
      category_id: this.selectedCategory()?.id,
    };

    return this.orgService.updateOrgCategory(category);
  }

  async updateOrgAddress() {
    const address: OrgAddress = {
      id: this.orgAddress()?.id,
      country: this.selectedCountry()?.id,
      state_province: this.selectedCity()?.state_id,
      city: this.selectedCity()?.id,
      street_address: this.description(),
    };

    const orgData: Org = {
      country: this.selectedCountry()?.id!,
    };

    try {
      await this.orgService.updateOrg(orgData);
      await this.orgService.updateOrgAddress(address);
      this.loading.set(false);
      this.isEditAddress.set(false);
      // this.router.navigateByUrl('/onboarding/add-accounts');
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async updateOrgContact() {
    this.loading.set(true);
    const address: OrgContact = {
      id: this.phoneNumberID(),
      phone_number: this.phoneNumberFormatted(),
      phone_number_display: this.phoneNumberDisplay(),
      is_phone_number: true,
      org: this.orgService.org()!.id,
    };

    try {
      if (this.email()) {
        const addressEmail: OrgContact = {
          id: this.emailID(),
          org: this.orgService.org()!.id,
          email: this.email(),
        };
        await this.orgService.updateOrgContact(addressEmail);
        this.loading.set(false);
        this.isEditContact.set(false);
      }

      return this.orgService.updateOrgContact(address);
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  phoneNumberChange(event?: any) {
    if (this.phoneNumber() === null || this.phoneNumber() === '0') {
      return;
    }

    // const starts_with = this.country()?.starts_with;
    let number: string = this.phoneNumber()?.split(' ').join('') || '';
    number = number.replace(/[^0-9]/g, '');
    const intNumber: number = parseInt(number);
    const numberString: string = intNumber.toString();

    if (isNaN(intNumber) || intNumber == 0) {
      this.phoneNumber.set(null);
      return;
    }
    this.phoneNumber.set(numberString);
    return;
  }

  formatPhoneNumberOnPaste(event: ClipboardEvent) {
    // console.log('paste event:', event);
    event.preventDefault();

    let pastedText = event.clipboardData?.getData('text');
    pastedText = pastedText?.split(' ')?.join('');

    this.phoneNumber.set(pastedText ?? '');
    this.phoneNumberChange();
  }

  async getCountries() {
    const countries = await this.locales.getAllCountries();
    this.countries.set(countries);
  }

  async getCities(isInit: boolean = false) {
    this.loading.set(true);
    this.citiesLoaded.set(false);
    if (!isInit) {
      this.selectedCity.set(null);
    }
    const cities = await this.locales.getCountryCitiesByCode(
      this.selectedCountry()!.code!,
    );
    this.cities.set(cities);
    this.loading.set(false);
    this.citiesLoaded.set(true);
  }

  async openCategoryModal() {
    this.modalService.open('select-category');
    await this.getCategories();
  }

  async getCategories() {
    this.loading.set(true);
    this.categoriesLoaded.set(false);
    const categories = await this.orgService.getAllCategories();

    if (categories) {
      this.categories.set(categories);
    } else {
      this.categories.set([]);
    }

    this.loading.set(false);
    this.categoriesLoaded.set(true);
  }

  back() {
    this.isOrgAddress.set(false);
    this.isOrgProfile.set(true);
  }

  addOrgAddress() {
    this.isOrgAddress.set(false);
    this.isOrgProfile.set(false);
  }

  openModal(modal: string) {
    this.modalService.open(modal);
  }

  closeModal(modal: string) {
    this.modalService.close(modal);
  }

  resetEditStates() {
    this.isEdit.set(false);
    this.isEditAddress.set(false);
    this.isEditContact.set(false);
    this.isEditWifi.set(false);
  }

  async resetStates() {
    // this.firstName.set('');
    // this.lastName.set('');
    // this.email.set('');
    this.loading.set(true);
    await this.orgService.getUserOrgs();
    this.loading.set(false);
    this.resetEditStates();
    this.isProfile.set(true);
    this.isPreview.set(false);
    this.isBasicProfile.set(false);
    this.isOrgAddress.set(false);
    this.isOrgContact.set(false);
    this.isOrgWifi.set(false);
  }
}
